/* =========================================
   SECTION: Product Form (Shopify 2.0)
   
   Handles Add to Cart form submission via AJAX.
   On success, delegates rendering to the cart drawer
   (or cart notification / cart page redirect).
   
   Interface contract with CartDrawer:
   - cart.getSectionsToRender() → [{id, section, selector}]
   - cart.renderContents(response) → updates DOM and opens drawer
   - cart.setActiveElement(element) → for focus management
   
   DEPENDENCIES:
   - global.js (fetchConfig)
   - cart.js (CartItems base)
   - cart-drawer.js (CartDrawer)
   - window.routes.cart_add_url
========================================= */

"use strict";

if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.submitButton = this.querySelector('[type="submit"]');

        /* Resolve cart target: drawer > notification > null (redirect) */
        this.cart =
          document.querySelector("cart-drawer") ||
          document.querySelector("cart-notification");

        if (this.form) {
          this.setupForm();
        }

        if (this.cart) {
          this.submitButton?.setAttribute("aria-haspopup", "dialog");
        }
      }

      setupForm() {
        /* Ensure variant ID input is enabled */
        const idInput = this.form.querySelector("[name=id]");
        if (idInput) idInput.disabled = false;

        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
      }

      async onSubmitHandler(event) {
        event.preventDefault();

        if (this.submitButton?.getAttribute("aria-disabled") === "true") {
          return;
        }

        this.handleErrorMessage();
        this.submitButton?.setAttribute("aria-disabled", true);
        this.submitButton?.classList.add("loading");

        const spinner = this.querySelector(".loading-overlay__spinner");
        spinner?.classList.remove("hidden");

        CartLogger?.logOperation?.("addToCart", {
          variantId: this.form.querySelector("[name=id]")?.value
        });

        /* Build the request */
        const config = fetchConfig("javascript");
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append(
            "sections",
            this.cart
              .getSectionsToRender()
              .map((section) => section.id)
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        try {
          const response = await fetch(
            `${routes.cart_add_url}`,
            config
          );
          const data = await response.json();

          if (data.status) {
            /* Shopify returned an error (e.g. variant unavailable) */
            this.handleErrorMessage(data.description);
            CartLogger?.warn?.("Add to cart error:", data.description);
            return;
          }

          if (!this.cart) {
            /* No cart drawer/notification — redirect to cart page */
            window.location = window.routes.cart_url;
            return;
          }

          /* Handle quick-add modal: wait for modal close first */
          const quickAddModal = this.closest("quick-add-modal");
          if (quickAddModal) {
            document.body.addEventListener(
              "modalClosed",
              () => {
                setTimeout(() => {
                  this.cart.renderContents(data);
                });
              },
              { once: true }
            );
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(data);
          }

          this.error = false;
          CartLogger?.info?.("Product added to cart successfully");
        } catch (error) {
          CartLogger?.error?.("Add to cart failed:", error);
        } finally {
          this.submitButton?.classList.remove("loading");

          if (
            this.cart &&
            this.cart.classList.contains("is-empty")
          ) {
            this.cart.classList.remove("is-empty");
          }

          if (!this.error) {
            this.submitButton?.removeAttribute("aria-disabled");
          }

          spinner?.classList.add("hidden");
        }
      }

      /**
       * Shows or hides the error message wrapper on the product form.
       * @param {string|false} errorMessage - message to show, or false to hide
       */
      handleErrorMessage(errorMessage = false) {
        const wrapper = this.querySelector(
          ".product-form__error-message-wrapper"
        );
        if (!wrapper) return;

        const messageEl = wrapper.querySelector(
          ".product-form__error-message"
        );
        wrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage && messageEl) {
          messageEl.textContent = errorMessage;
        }
      }
    }
  );
}
